import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { DateTimePicker } from '../components/DateTimePicker';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { BandSearchSelect } from '../components/filters/BandSearchSelect';
import { Users, Loader2, Mail, Phone, MapPin, Calendar, User } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import type { Band } from '../types/database';

const bookBandSchema = z.object({
  band_id: z.string().uuid('Please select a band'),
  request_text: z.string().min(10, 'Please provide more details about your request').max(1000, 'Request text is too long'),
  desired_date: z.date().min(new Date(), { message: "Event date must be in the future" }),
  desired_location: z.string().min(3, 'Please specify the event location'),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }).nullable(),
  name: z.string().min(2, 'Please enter your name').max(100, 'Name is too long'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  address: z.string().optional(),
});

type BookBandFormData = z.infer<typeof bookBandSchema>;

function BookBand() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [bands, setBands] = useState<Band[]>([]);
  const [selectedBand, setSelectedBand] = useState<Band | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<BookBandFormData>({
    resolver: zodResolver(bookBandSchema),
    defaultValues: {
      coordinates: null,
    },
  });

  useEffect(() => {
    async function loadBands() {
      try {
        const { data: bandsData, error: bandsError } = await supabase
          .from('bands')
          .select('*')
          .order('name');

        if (bandsError) throw bandsError;
        setBands(bandsData || []);

        // If slug is provided, find and select that band
        if (slug) {
          const band = bandsData?.find(b => b.slug === slug);
          if (band) {
            setSelectedBand(band);
            setValue('band_id', band.id);
          }
        }
      } catch (err) {
        console.error('Error loading bands:', err);
        setError('Failed to load bands');
      } finally {
        setLoading(false);
      }
    }

    loadBands();
  }, [slug, setValue]);

  const handleLocationChange = (place: { name: string; coordinates: { latitude: number; longitude: number } }) => {
    setValue('desired_location', place.name);
    setValue('coordinates', place.coordinates);
  };

  const handleBandSelect = (band: Band | null) => {
    setSelectedBand(band);
    setValue('band_id', band?.id || '');
  };

  const onSubmit = async (data: BookBandFormData) => {
    if (submitting) return; // Prevent multiple submissions
    
    try {
      setSubmitting(true);
      setError('');

      // Get band data
      const { data: bandData, error: bandError } = await supabase
        .from('bands')
        .select('*')
        .eq('id', data.band_id)
        .single();

      if (bandError) throw bandError;
      if (!bandData) throw new Error('Band not found');

      // Send booking request email via edge function
      const { error: emailError } = await supabase.functions.invoke('send-booking-request', {
        body: {
          bandName: bandData.name,
          bandAdminProfileId: bandData.created_by,
          requestData: {
            ...data,
            bandName: bandData.name,
            desiredDate: data.desired_date.toISOString(),
          },
        },
      });

      if (emailError) throw emailError;

      setSuccess(true);
      reset();
      setSelectedBand(null);
    } catch (err) {
      console.error('Error sending booking request:', err);
      setError(err instanceof Error ? err.message : 'Failed to send booking request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    ...(selectedBand ? [{ label: selectedBand.name, href: `/bands/${selectedBand.slug}` }] : []),
    { label: t('booking.title') },
  ];

  return (
    <>
      <SEOHead
        title={`${t('booking.title')} - ${selectedBand?.name || 'Band'}`}
        description={t('booking.seoDescription')}
        keywords={['Band buchen', 'Band booking', 'Event booking', 'Konzert buchen']}
        url={`https://jamnation.ch/book-band${slug ? `/${slug}` : ''}`}
        noIndex={true}
      />

      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <Title subtitle={t('booking.subtitle')}>
          {t('booking.title')}
        </Title>

        <div className="max-w-4xl mx-auto">
          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 text-green-500 p-4 rounded-lg mb-6 text-center">
              <h3 className="font-semibold mb-2">{t('booking.success.title')}</h3>
              <p>{t('booking.success.message')}</p>
              <button
                onClick={() => navigate('/bands')}
                className="mt-4 btn-outline"
              >
                {t('booking.success.backToBands')}
              </button>
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Band Selection */}
              <div className="panel">
                <h3 className="text-xl font-semibold mb-6 flex items-center">
                  {t('booking.form.bandSelection')}
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-light/80 mb-2">
                    {t('booking.form.band')} *
                  </label>
                  <BandSearchSelect
                    bands={bands}
                    selectedBand={selectedBand}
                    onSelectBand={handleBandSelect}
                    placeholder={t('booking.form.selectBand')}
                    error={errors.band_id?.message}
                  />
                </div>
              </div>

              {/* Event Details */}
              <div className="panel">
                <h3 className="text-xl font-semibold mb-6 flex items-center">
                  {t('booking.form.eventDetails')}
                </h3>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="request_text" className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.requestText')} *
                    </label>
                    <textarea
                      id="request_text"
                      rows={6}
                      {...register('request_text')}
                      placeholder={t('booking.form.requestTextPlaceholder')}
                      className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                    />
                    {errors.request_text && (
                      <p className="mt-1 text-sm text-red-500">{errors.request_text.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.desiredDate')} *
                    </label>
                    <DateTimePicker
                      selected={watch('desired_date')}
                      onChange={(date) => setValue('desired_date', date)}
                      minDate={new Date()}
                      error={errors.desired_date?.message}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.desiredLocation')} *
                    </label>
                    <LocationAutocomplete
                      value={watch('desired_location') || ''}
                      onChange={handleLocationChange}
                      error={errors.desired_location?.message}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="panel">
                <h3 className="text-xl font-semibold mb-6 flex items-center">
                  {t('booking.form.contactInfo')}
                </h3>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name')}
                      placeholder={t('booking.form.namePlaceholder')}
                      className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email')}
                      placeholder={t('booking.form.emailPlaceholder')}
                      className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.phone')} ({t('booking.form.optional')})
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone')}
                      placeholder={t('booking.form.phonePlaceholder')}
                      className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-light/80 mb-2">
                      {t('booking.form.address')} ({t('booking.form.optional')})
                    </label>
                    <input
                      type="text"
                      id="address"
                      {...register('address')}
                      placeholder={t('booking.form.addressPlaceholder')}
                      className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-end md:space-x-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn-outline order-2 mt-6 md:mt-0"
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-secondary md:order-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="flex items-center space-x-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('booking.form.sending')}</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-2">
                      <Mail className="w-5 h-5" />
                      <span>{t('booking.form.sendRequest')}</span>
                    </span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

export default BookBand;